import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, LabelList } from "recharts";
import { BASE_URL } from "../../../config";

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

// Short "$1.1M"/"$775K" form for the bar labels on narrow screens -- with 12 months of
// full "$775,126"-style labels, there isn't enough room between bars and they overlap.
const formatCompactCurrency = (value) => {
  const n = Number(value) || 0;
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${Math.round(n / 1000)}K`;
  return `$${Math.round(n)}`;
};

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => currentYear - i);

/** Standalone Salary card with its own Year dropdown (independent of the shared PeriodFilterBar) —
 * reuses the backend's shared getMonthlySalary() logic via /salary-trend rather than duplicating it. */
export default function SalaryTrendCard({ getAuthHeaders, divisionId }) {
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState({ total: 0, monthly: [] });
  const [isNarrowScreen, setIsNarrowScreen] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 640 : false
  );
  useEffect(() => {
    const update = () => setIsNarrowScreen(window.innerWidth < 640);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams({ year });
    if (divisionId && divisionId !== 'all') params.append('department', divisionId);

    fetch(`${BASE_URL}/api/dashboard/salary-trend?${params}`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.success) setData(res.data);
        else console.error('Salary trend failed:', res.message);
      })
      .catch((err) => console.error('Salary trend fetch error:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, divisionId]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 overflow-hidden">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold text-gray-900">Salary</h2>
        <div className="relative">
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="appearance-none rounded-full border border-gray-200 text-xs pl-3 pr-7 py-1 bg-white outline-none cursor-pointer focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-blue-500"
          >
            {YEAR_OPTIONS.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <ChevronDown size={12} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
        </div>
      </div>

      <div className="border-t border-gray-200 pt-3">
        <div className="text-2xl font-bold text-gray-900">{formatCurrency(data.total)}</div>
        <p className="text-xs text-gray-500 mb-1">Monthly Employee Salary Trend</p>

        <ResponsiveContainer width="100%" height={190}>
          <BarChart data={data.monthly} margin={{ top: 20, left: 0, right: 0, bottom: 0 }} barCategoryGap="20%">
            <defs>
              <linearGradient id="salaryGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#AEDFF7" stopOpacity={1} />
                <stop offset="50%" stopColor="#42A5F5" stopOpacity={1} />
                <stop offset="100%" stopColor="#0D47A1" stopOpacity={0.6} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="month"
              axisLine={{ stroke: '#e5e7eb' }}
              tickLine={false}
              tick={{ fontSize: isNarrowScreen ? 9 : 12, fill: '#6b7280' }}
              tickMargin={8}
              interval={isNarrowScreen ? 0 : undefined}
            />
            <YAxis hide domain={[0, (max) => max * 1.2]} />
            <Bar dataKey="total_salary" fill="url(#salaryGradient)" radius={[4, 4, 0, 0]} barSize={isNarrowScreen ? 8 : 14}>
              <LabelList
                dataKey="total_salary"
                position="top"
                offset={6}
                formatter={isNarrowScreen ? formatCompactCurrency : (value) => `$${Math.round(value).toLocaleString()}`}
                style={{ fontSize: isNarrowScreen ? 8 : 12, fill: '#374151' }}
                angle={isNarrowScreen ? -60 : 0}
                textAnchor={isNarrowScreen ? 'start' : 'middle'}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
